import React, { useEffect, useState } from 'react';
import './App.css';

import {
    BrowserRouter as Router,
    Routes,
    Route,
    Outlet,
    Navigate,
    Link
} from "react-router-dom";

import Home from './Home/Home';
import History from './History/History';
import Rules from './Rules/Rules';
import CurrentSeason from './CurrentSeason/CurrentSeason';
import Draft from './Draft/Draft';
import TradeHistory from './TradeHistory/TradeHistory';
import PlayerList from './PlayerList/PlayerList';
import PlayerPage from './Player/PlayerPage';
import Login from './Login/Login';
import ForgotPassword from './Login/ForgotPassword';
import ResetPassword from './Login/ResetPassword';
import LoginWithLink from './Login/LoginWithLink';
import MagicLinkConfirm from './Login/MagicLinkConfirm';

import Loader from './lib/components/Loader';
import NavLinkCustomV2 from './lib/components/NavLinkCustomV2';

import api from './utils/api';
import constants from './lib/constants';

import League from './League/Leaguev2';
import Trade from './Trade/Trade';
import TradeV2 from './Trade/Tradev2';
import Transactions from './Transactions/Transactions';
import Admin from './Admin';
import SignUpForm from './Login/SignUp';
import Users from './User/Users';
import UserEdit from './Admin/UserEdit';
import DraftLobby from './DraftLobby';
import Scoreboard from './Scoreboard';
import ScrollToTop from './lib/components/ScrollToTop';
import MyTeam from './MyTeam';
import DraftPicks from './MyTeam/DraftPicks';
import Players from './MyTeam/Players';
import PendingTrades from './MyTeam/PendingTrades';
import More from './More';

const AdminRoutes = ({ loadingUserData, userData }) => {
    if (loadingUserData) {
        return <Loader/>;
    }
    return userData.permissions === 'admin' ? <Outlet/> : <Navigate to='/home'/>
}

const PicksAndPlayersRoutes = ({ playerData, outgoingPicks }) => {
    if (playerData && outgoingPicks) {
        return <Outlet/>;
    }

    return <Loader/>
}

const App = () => {
    const [playerData, updatePlayerData] = useState(null);
    const [outgoingPicks, updateOutgoingPicks] = useState(null);
    const [userData, updateUserData] = useState({});
    const [loadingUserData, updateLoadingUserData] = useState(true);

    const fetchPlayerData = () => {
        api.get('/api/v3/players').then(({ data }) => {
            updatePlayerData(data.players);
        }).catch(res => {
            console.error('Error retrieving players: ' + res);
        });
    };

    useEffect(fetchPlayerData, []);

    useEffect(() => {
        api.get('/api/v1/outgoingPicks').then(({ data }) => {
            updateOutgoingPicks(data);
        }).catch(res => {
            updateOutgoingPicks(null);
            console.error('Error retrieving outgoing pick data: ' + res);
        });
    }, []);

    useEffect(() => {
        updateLoadingUserData(true);
        api.get('/api/v1/user').then(({ data }) => {
            updateUserData(data);
            updateLoadingUserData(false);
        }).catch(err => {
            updateUserData({});
            updateLoadingUserData(false);
            console.error('Error retrieving user data: ' + err);
        });
    }, [])

    const isLoggedIn = !!userData.username;

    return (
        <div id="appContainer">
            <Router>
                <ScrollToTop/>
                <div>
                    <div id="mainNav">
                        <div className="d-flex justify-content-between align-items-center">
                            <div style={{ marginLeft: '5px', flex: '1' }}>
                                <div>&#127881; Taylor (Pitts + Puka)</div>
                                <div style={{fontSize: '0.75rem'}}>Season 9, 2025 Champ</div>
                            </div>
                            <Link to="/home" className="header-logo-center">
                                <i className="bi bi-trophy-fill"></i>
                            </Link>
                            <div style={{ flex: '1', textAlign: 'right' }}>
                                {isLoggedIn ? 
                                    <button className="btn"><Link to="/user"><i className="bi bi-person-circle text-white fs-2"></i></Link></button> 
                                    : 
                                    <Link to="/login"><button className="btn btn-secondary">Log in</button></Link>
                                }
                            </div>
                        </div>
                        <nav 
                            className="navbar fixed-bottom nav-justified nav-pills flex-nowrap align-items-stretch overflow-auto bottom-navbar-clean"
                        >
                            <div className="nav-item">
                                <NavLinkCustomV2 
                                    route={isLoggedIn ? "/myTeam" : "/login"} 
                                    text={<><i className="bi bi-people-fill"></i><span className="nav-label">My Team</span></>} 
                                />
                            </div>
                            <div className="nav-item">
                                <NavLinkCustomV2 route="/scoreboard" text={<><i className="bi bi-graph-up"></i><span className="nav-label">Scoreboard</span></>} />
                            </div>
                            <div className="nav-item">
                                <NavLinkCustomV2 route="/league" text={<><i className="bi bi-list-ul"></i><span className="nav-label">Rosters</span></>} />
                            </div>
                            <div className="nav-item">
                                <NavLinkCustomV2 route="/draft" text={<><i className="bi bi-trophy"></i><span className="nav-label">Drafts</span></>} />
                            </div>
                            <div className="nav-item">
                                <NavLinkCustomV2 route="/more" text={<><i className="bi bi-three-dots"></i><span className="nav-label">More</span></>} />
                            </div>
                        </nav>
                    </div>
                    <div id="container" className="container mt-1" style={{ marginBottom: '250px' }}>
                        <Routes>
                            <Route exact path="/" element={<Home isLoggedIn={isLoggedIn}/>}/>
                            <Route path="/home" element={<Home isLoggedIn={isLoggedIn}/>}/>
                            <Route path="/scoreboard" element={<Scoreboard/>}/>
                            <Route path="/history/*" element={<History/>}/>
                            <Route path="/rules" element={<Rules/>}/>
                            <Route element={<PicksAndPlayersRoutes playerData={playerData} outgoingPicks={outgoingPicks}/>}>
                                <Route
                                    path="/league"
                                    element={<Navigate to={`/league/${constants.managerList[0]}`}/>}
                                />
                                <Route 
                                    path="/league/:managerName"  
                                    element={<League userData={userData} outgoingPicks={outgoingPicks} playerData={playerData}/>}
                                />
                            </Route>
                            <Route path="/draft" element={playerData && outgoingPicks ? <Draft outgoingPicks={outgoingPicks} playerData={playerData}/> : <Loader/>}/>
                            <Route path="/draftLobby" element={playerData && !loadingUserData ? 
                                <DraftLobby userData={userData} playerData={playerData} fetchPlayerData={fetchPlayerData}/> : 
                                <Loader/>
                            }/>
                            <Route path="/trades" element={playerData ? <TradeHistory playerData={playerData}/> : <Loader/>}/>
                            <Route path="/players" element={playerData ? <PlayerList playerData={playerData}/> : <Loader/>}/>
                            <Route path="/player/:player_id" element={<PlayerPage/>}/>
                            <Route path="/login" element={<Login loadingUserData={loadingUserData} userData={userData} updateUserData={updateUserData}/>}/>
                            <Route path="/forgot-password" element={<ForgotPassword/>}/>
                            <Route path="/reset-password/:token" element={<ResetPassword/>}/>
                            <Route path="/login-link" element={<LoginWithLink/>}/>
                            <Route path="/magic-link/:token" element={<MagicLinkConfirm updateUserData={updateUserData}/>}/>
                            <Route path="/transactions" element={playerData ? <Transactions playerData={playerData}/> : <Loader/>}/>
                            <Route path="/trade" element={playerData && outgoingPicks && userData ? <TradeV2 outgoingPicks={outgoingPicks} playerData={playerData} userData={userData}/> : <Loader/>}/>
                            <Route element={<AdminRoutes userData={userData} loadingUserData={loadingUserData}/>}>
                                <Route path="/admin" element={<Admin/>}/>
                                <Route path="/admin/trade" element={playerData && outgoingPicks ? <Trade outgoingPicks={outgoingPicks} playerData={playerData}/> : <Loader/>}/>
                                <Route path="/admin/userEdit/:id" element={<UserEdit/>}/>
                            </Route>
                            <Route path="/signup" element={<SignUpForm updateUserData={updateUserData}/>}/>
                            <Route path="/user" element={
                                loadingUserData ? 
                                <Loader/> :  <Users userData={userData} updateUserData={updateUserData}/>}/>

                            <Route path="/myTeam" 
                                element={
                                    playerData && outgoingPicks ? <MyTeam userData={userData} playerData={playerData} outgoingPicks={outgoingPicks}/> : <Loader/>
                                }
                            >
                                <Route path="draftPicks" element={<DraftPicks outgoingPicks={outgoingPicks}/>}/>
                                <Route path="players" element={<Players playerData={playerData}/>}/>
                                <Route path="pendingTrades" element={<PendingTrades/>}/>
                            </Route>
                            <Route path="/more" element={<More userData={userData} isLoggedIn={isLoggedIn} />}/>
                        </Routes>
                    </div>
                </div>
            </Router>
        </div>
    );
};

export default App;
